import { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { useSettings } from "app/hooks/settings";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import _ from "lodash";
import { AuthContext } from "app/providers/auth";

const JoinRoomSchema = z.object({
  id: z.string().min(1, "Room ID is required"),
  pin_code: z.string().min(1, "Pin code is required"),
});

const SettingsFormSchema = z.object({
  nok_can_create_activities: z.boolean().default(false).optional(),
  enable_call_me_back_for_patients: z.boolean().default(false).optional(),
  track_media_roles: z.boolean().default(false).optional(),
  disable_start_activity_from_calendar: z.boolean().default(false).optional(),
  enable_chat_feature: z.boolean().default(false).optional(),
  categories_icons_dataset: z.enum(["default", "custom"]).default("default"),
  video_settings: z
    .object({
      type: z.enum(["whereby", "join", "delta"]),
      join: z
        .object({
          rooms: z.array(JoinRoomSchema).default([]),
        })
        .optional(),
      delta: z
        .object({
          username: z.string().default("").optional(),
          password: z.string().default("").optional(),
          templateId: z.string().default("").optional(),
        })
        .optional(),
    })
    .default({ type: "whereby" }),
});

// defaultValues: activeSettings ?? patientSettingsDefault,
export function Settings() {
  const [settings, updateSettings] = useSettings();
  const authContext = useContext(AuthContext);

  const { t } = useTranslation();
  const form = useForm<z.infer<typeof SettingsFormSchema>>({
    resolver: zodResolver(SettingsFormSchema),
  });

  useEffect(() => {
    form.reset(settings as any);
  }, [settings, form]);

  async function onSubmit(data: z.infer<typeof SettingsFormSchema>) {
    const changed = _.fromPairs(
      _.differenceWith(_.toPairs(data), _.toPairs(settings), _.isEqual)
    );
    if (Object.keys(changed).length === 0) return; // nothing to update
    // @ts-ignore
    await updateSettings(authContext.user.account_id, changed);
  }

  return (
    <Card>
      <CardHeader className="px-7">
        <CardTitle>{t("accountScreen.settings.header")}</CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="w-full space-y-6"
          >
            <div className="space-y-4">
              {[
                {
                  fieldName: "nok_can_create_activities" as any,
                  title: t(
                    "accountScreen.settings.nok_can_create_activities.title"
                  ),
                  description: t(
                    "accountScreen.settings.nok_can_create_activities.description"
                  ),
                },
                {
                  fieldName: "enable_call_me_back_for_patients" as any,
                  title: t(
                    "accountScreen.settings.enable_call_me_back_for_patients.title"
                  ),
                  description: t(
                    "accountScreen.settings.enable_call_me_back_for_patients.description"
                  ),
                },
                {
                  fieldName: "track_media_roles" as any,
                  title: t("accountScreen.settings.track_media_roles.title"),
                  description: t(
                    "accountScreen.settings.track_media_roles.description"
                  ),
                },
                {
                  fieldName: "disable_start_activity_from_calendar" as any,
                  title: t(
                    "accountScreen.settings.disable_start_activity_from_calendar.title"
                  ),
                  description: t(
                    "accountScreen.settings.disable_start_activity_from_calendar.description"
                  ),
                },
                {
                  fieldName: "enable_chat_feature" as any,
                  title: t("accountScreen.settings.enable_chat_feature.title"),
                  description: t(
                    "accountScreen.settings.enable_chat_feature.description"
                  ),
                },
              ].map((feature) => (
                <FormField
                  control={form.control}
                  name={feature.fieldName}
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">
                          {feature.title}
                        </FormLabel>
                        <FormDescription>{feature.description}</FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              ))}

              {/* ========== */}
              <FormField
                control={form.control}
                name="categories_icons_dataset"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {t(
                          "accountScreen.settings.categories_icons_dataset.title"
                        )}
                      </FormLabel>
                      <FormDescription>
                        {t(
                          "accountScreen.settings.categories_icons_dataset.description"
                        )}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <RadioGroup
                        id="categories_icons_dataset_group"
                        className="border-0 gap-1 flex flex-col"
                        value={field.value}
                        onValueChange={(value) => {
                          field.onChange(value);
                        }}
                      >
                        <div className="flex items-center space-x-2 border-0 my-0">
                          <RadioGroupItem
                            className="text-[#1949a3]"
                            value="default"
                            id="id_default"
                          />
                          <Label
                            className="text-sm font-normal text-[#11203D]"
                            htmlFor="id_default"
                          >
                            {t(
                              "accountScreen.settings.categories_icons_dataset.values.default"
                            )}
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2 border-0 my-0">
                          <RadioGroupItem
                            className="text-[#1949a3]"
                            value="custom"
                            id="id_custom"
                          />
                          <Label
                            className="text-sm font-normal text-[#11203D]"
                            htmlFor="id_custom"
                          >
                            {t(
                              "accountScreen.settings.categories_icons_dataset.values.custom"
                            )}
                          </Label>
                        </div>
                      </RadioGroup>
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
            {/* ========== VIDEO SETTINGS ========== */}
            <FormField
              control={form.control}
              name="video_settings"
              render={({ field }) => {
                const value = field.value || { type: "whereby" };
                return (
                  <FormItem className="flex flex-col rounded-lg border p-4">
                    <FormLabel className="text-base">
                      {t(
                        "accountScreen.settings.video_settings.title",
                        "Video settings"
                      )}
                    </FormLabel>
                    <FormDescription>
                      {t(
                        "accountScreen.settings.video_settings.description",
                        "Configure video integration type and options."
                      )}
                    </FormDescription>
                    <FormControl>
                      <RadioGroup
                        value={value.type}
                        onValueChange={(type) => {
                          // Reset join/delta fields when type changes
                          if (type === "whereby") field.onChange({ type });
                          if (type === "join")
                            field.onChange({
                              type,
                              join: { rooms: [{ id: "", pin_code: "" }] },
                            });
                          if (type === "delta")
                            field.onChange({
                              type,
                              delta: { username: "", password: "" },
                            });
                        }}
                        className="flex flex-row gap-4 mt-2"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem
                            value="whereby"
                            id="video_type_whereby"
                          />
                          <Label htmlFor="video_type_whereby">Whereby</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="join" id="video_type_join" />
                          <Label htmlFor="video_type_join">Join</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="delta" id="video_type_delta" />
                          <Label htmlFor="video_type_delta">Delta</Label>
                        </div>
                      </RadioGroup>
                    </FormControl>
                    {/* JOIN ROOMS */}
                    {value.type === "join" && (
                      <div className="mt-4">
                        <Label>
                          {t(
                            "accountScreen.settings.video_settings.join_rooms",
                            "Rooms"
                          )}
                        </Label>
                        {(value.join?.rooms || [{ id: "", pin_code: "" }]).map(
                          (
                            room: { id: string; pin_code: string },
                            idx: number
                          ) => (
                            <div
                              key={idx}
                              className="flex items-center gap-2 mt-2"
                            >
                              <Input
                                value={room.id}
                                onChange={(e) => {
                                  const newRooms = [
                                    ...(value.join?.rooms || []),
                                  ];
                                  newRooms[idx] = {
                                    ...newRooms[idx],
                                    id: e.target.value,
                                  };
                                  field.onChange({
                                    ...value,
                                    join: { rooms: newRooms },
                                  });
                                }}
                                placeholder={t(
                                  "accountScreen.settings.video_settings.room_id_placeholder",
                                  "Room ID"
                                )}
                                className="w-1/2"
                              />
                              <Input
                                value={room.pin_code}
                                type="password"
                                onChange={(e) => {
                                  const newRooms = [
                                    ...(value.join?.rooms || []),
                                  ];
                                  newRooms[idx] = {
                                    ...newRooms[idx],
                                    pin_code: e.target.value,
                                  };
                                  field.onChange({
                                    ...value,
                                    join: { rooms: newRooms },
                                  });
                                }}
                                placeholder={t(
                                  "accountScreen.settings.video_settings.room_pin_placeholder",
                                  "Pin code"
                                )}
                                className="w-1/2"
                              />
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  const newRooms = [
                                    ...(value.join?.rooms || []),
                                  ];
                                  newRooms.splice(idx, 1);
                                  field.onChange({
                                    ...value,
                                    join: {
                                      rooms: newRooms.length
                                        ? newRooms
                                        : [{ id: "", pin_code: "" }],
                                    },
                                  });
                                }}
                                disabled={(value.join?.rooms?.length || 0) <= 1}
                              >
                                {t("common.remove", "Remove")}
                              </Button>
                            </div>
                          )
                        )}
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          className="mt-2"
                          onClick={() => {
                            const newRooms = [...(value.join?.rooms || [])];
                            newRooms.push({ id: "", pin_code: "" });
                            field.onChange({
                              ...value,
                              join: { rooms: newRooms },
                            });
                          }}
                        >
                          {t("common.add", "Add room")}
                        </Button>
                      </div>
                    )}
                    {/* DELTA USERNAME/PASSWORD */}
                    {value.type === "delta" && (
                      <div className="mt-4 flex flex-col gap-2">
                        <Label>
                          {t(
                            "accountScreen.settings.video_settings.delta_username",
                            "Username"
                          )}
                        </Label>
                        <Input
                          value={value.delta?.username || ""}
                          onChange={(e) => {
                            field.onChange({
                              ...value,
                              delta: {
                                ...value.delta,
                                username: e.target.value,
                              },
                            });
                          }}
                          placeholder={t(
                            "accountScreen.settings.video_settings.delta_username_placeholder",
                            "Enter username"
                          )}
                        />
                        <Label>
                          {t(
                            "accountScreen.settings.video_settings.delta_password",
                            "Password"
                          )}
                        </Label>
                        <Input
                          type="password"
                          value={value.delta?.password || ""}
                          onChange={(e) => {
                            field.onChange({
                              ...value,
                              delta: {
                                ...value.delta,
                                password: e.target.value,
                              },
                            });
                          }}
                          placeholder={t(
                            "accountScreen.settings.video_settings.delta_password_placeholder",
                            "Enter password"
                          )}
                        />
                        <Label>
                          {t(
                            "accountScreen.settings.video_settings.delta_template_id",
                            "Template ID"
                          )}
                        </Label>
                        <Input
                          value={value.delta?.templateId || ""}
                          onChange={(e) => {
                            field.onChange({
                              ...value,
                              delta: {
                                ...value.delta,
                                templateId: e.target.value,
                              },
                            });
                          }}
                          placeholder={t(
                            "accountScreen.settings.video_settings.delta_template_id_placeholder",
                            "Enter template ID"
                          )}
                        />
                      </div>
                    )}
                  </FormItem>
                );
              }}
            />
            {/* ========== */}
            <Button className="" type="submit">
              {t("common.save")}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
